import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { OFFER_APR, OFFER_DISCLOSURE, OFFER_DOWN_PAYMENT, OFFER_EXPIRES, OFFER_MODEL, OFFER_PAYMENT, OFFER_TERM_MONTHS, TEASER_PAYMENT } from '../config';
import { IconWallet } from './icons';
import { scrollToLeadForm } from '../utils/scroll';

/**
 * The hero's attention hook.
 *
 * Renders the advertised payment ONLY when the dealership has supplied a complete,
 * written offer: amount, model, term, down payment, APR, expiration, and the verbatim
 * disclosure. A payment is a TILA / Reg Z triggering term, so a partial offer renders
 * nothing at all — an amount without its terms is the non-compliant case.
 *
 * Until then it shows a no-figure hook that carries the same economic promise
 * ("find out what you can pay") without advertising credit terms.
 */
const hasPayment = Boolean(OFFER_PAYMENT);
const termsAreComplete = Boolean(OFFER_MODEL && OFFER_TERM_MONTHS && OFFER_DOWN_PAYMENT && OFFER_APR && OFFER_EXPIRES && OFFER_DISCLOSURE);
export function HeroHook() {
  const {
    t
  } = useLanguage();
  const reduce = useReducedMotion();
  if (hasPayment) {
    return <button type="button" onClick={scrollToLeadForm} className="group block w-full text-left transition-transform duration-200 hover:-translate-y-0.5" data-id="element-147" data-mp="1y1lgaonprrg9">
        {/* Kia red banner: this is the one place a figure earns a filled red block,
            because the dealership authorized the offer and its terms ride with it. */}
        <span className="block bg-kred px-5 py-5 sm:px-6" data-id="element-148" data-mp="2gzejzt65w5ep">
          <span className="flex items-center gap-2" data-id="element-149" data-mp="2gwsb9q7jbjuw">
            <IconWallet className="h-5 w-5 shrink-0 text-white/80" data-id="element-150" data-mp="2c0uwhobx6lk8" />
            <span className="font-display text-[10px] font-bold uppercase tracking-[0.24em] text-white/80" data-id="element-151" data-mp="mklpjxh0lkoc">
              {OFFER_MODEL || t('offer.eyebrowFallback')}
            </span>
          </span>
          <span className="mt-2 flex flex-wrap items-baseline gap-x-2" data-id="element-152" data-mp="2hoa19r89ddlh">
            <span className="font-display text-[11px] font-bold uppercase tracking-[0.2em] text-white/80" data-id="element-153" data-mp="2hq4j2wo8gplc">
              {t('offer.from')}
            </span>
            <span className="font-display text-5xl font-extrabold leading-none tracking-[-0.04em] text-white sm:text-6xl" data-id="element-154" data-mp="2rl4v3wtl6ptf">
              <span className="align-top text-2xl sm:text-3xl" data-id="element-155" data-mp="2vp4a2az7xgko">$</span>
              {OFFER_PAYMENT}
            </span>
            <span className="font-display text-base font-bold text-white/85 sm:text-lg" data-id="element-156" data-mp="30l1qsiee8oxz">
              {termsAreComplete ? t('offer.perMonth', {
              n: OFFER_TERM_MONTHS
            }) : t('offer.perMo')}
            </span>
          </span>
          <span className="mt-3 inline-flex items-center gap-2 border-b-2 border-gold pb-0.5 text-sm font-bold text-white" data-id="element-157" data-mp="3fbirfzj7fatt">
            {t('hook.sub')}
            <span aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1" data-id="element-158" data-mp="3jf6zskfnuag4">
              →
            </span>
          </span>
        </span>

        {/* Terms and the dealership's disclosure, verbatim — never shortened.
            Until they exist, the strip says so in plain sight rather than letting a
            bare payment stand on its own. */}
        <span className="block border border-t-0 border-ink/10 bg-offwhite px-5 py-3 sm:px-6" data-id="element-159" data-mp="ypc2kzz103sg">
          {termsAreComplete ? <>
              <span className="block text-xs leading-relaxed text-body" data-id="element-160" data-mp="1vejsslalb12p">
                {t('offer.terms', {
              down: OFFER_DOWN_PAYMENT,
              apr: OFFER_APR,
              months: OFFER_TERM_MONTHS
            })}
              </span>
              <span className="mt-1 block text-[11px] leading-relaxed text-muted" data-id="element-161" data-mp="1hoq0fszvlyo8">
                {OFFER_DISCLOSURE} {t('offer.expires', {
              date: OFFER_EXPIRES
            })}
              </span>
            </> : <span className="block text-[11px] font-bold uppercase leading-relaxed tracking-[0.08em] text-kred" data-id="element-162" data-mp="2aclyb116lwml">
              {t('offer.pending')}
            </span>}
        </span>
      </button>;
  }

  // No-figure hook. As loud as the offer block, but it advertises no credit term.
  return <button type="button" onClick={scrollToLeadForm} className="group relative block w-full overflow-hidden border border-ink/10 bg-white px-5 py-5 text-left shadow-[0_18px_40px_-32px_rgba(5,20,31,0.55)] transition-transform duration-200 hover:-translate-y-0.5 sm:px-6" data-id="element-163" data-mp="1kxpv14pfsukc">
      {/* Kia red rule down the edge — accent, never a fill. */}
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[5px] bg-kred" data-id="element-164" data-mp="23vk6rqhy9k6f" />
      {/* Gold wash fading out to the right, so the block reads warm but stays white. */}
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-gold/20 to-transparent" data-id="element-165" data-mp="28siunrb0suw6" />

      {/* The figure leads; the supporting copy is deliberately quieter than it. */}
      <span className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6" data-id="element-166" data-mp="3aphth265etdi">
      {TEASER_PAYMENT && <span className="block sm:order-2 sm:shrink-0 sm:text-right" style={{
        perspective: 600
      }} data-id="element-167" data-mp="36nd5ok2dsqys">
          <span className="block font-display text-[10px] font-bold uppercase tracking-[0.22em] text-camelink" data-id="element-168" data-mp="3khnka8knz4r4">
            {t('offer.from')}
          </span>
          {/* Slow 3D breathing loop — never a hard blink, which would be a
              photosensitivity risk and would read as a broken element. */}
          <motion.span animate={reduce ? undefined : {
          rotateY: [-9, 9, -9],
          scale: [1, 1.04, 1],
          opacity: [0.88, 1, 0.88]
        }} transition={{
          duration: 3.2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} style={{
          transformStyle: 'preserve-3d',
          display: 'inline-block'
        }} className="mt-1 block font-display text-6xl font-extrabold leading-[0.85] tracking-[-0.05em] text-kred sm:text-[5.25rem]" data-id="element-169" data-mp="2nrmy8hboj2di">
            <span className="align-top text-2xl sm:text-4xl" data-id="element-170" data-mp="o20tsxtzpkbn">$</span>
            {TEASER_PAYMENT}
            <span className="font-display text-lg font-bold tracking-normal text-muted sm:text-xl" data-id="element-171" data-mp="t141kq1cu4nb">
              {t('offer.perMo')}
            </span>
          </motion.span>
        </span>}

        <span className="block sm:order-1" data-id="element-172" data-mp="cgcejdhiv4h">
          <span className="flex items-center gap-2" data-id="element-173" data-mp="dhqkwpsrxqf">
            <IconWallet className="h-4 w-4 shrink-0 text-kred" data-id="element-174" data-mp="5bj6x9vu9iy7" />
            <span className="font-display text-[9px] font-bold uppercase tracking-[0.22em] text-kred" data-id="element-175" data-mp="5bswwxgvvq3w">
              {t('hook.eyebrow')}
            </span>
          </span>

          <span className="mt-1.5 block font-display text-base font-extrabold uppercase leading-[1.1] tracking-[-0.01em] text-ink sm:text-xl" data-id="element-176" data-mp="f2e3b72styxd">
            {t('hook.title')}
          </span>

          <span className="mt-2 inline-flex items-center gap-2 border-b-2 border-gold pb-0.5 text-xs font-bold text-ink" data-id="element-177" data-mp="3ipnvbtwwmuuu">
            {t('hook.sub')}
            <span aria-hidden="true" className="text-kred transition-transform duration-200 group-hover:translate-x-1" data-id="element-178" data-mp="3b4u0r1nz4pkl">
              →
            </span>
          </span>
        </span>
      </span>
    </button>;
}